import React, { useState, useEffect } from 'react';
import { admin, claims, categories as categoriesApi, auth, settings } from '../services/api';
import { useNavigate } from 'react-router-dom';
import {
  Upload, Settings, Image, Save, AlertCircle,
  CheckCircle, Users, Shield, Home, User, Plus, Clock, CheckSquare, XSquare
} from 'lucide-react';
import { weddingColors } from '../styles/weddingTheme';

function EnhancedAdminPanel() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('providers');
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState(null);

  // Approvals State
  const [pendingClaims, setPendingClaims] = useState([]);
  const [approvalsLoading, setApprovalsLoading] = useState(false);

  // Add Provider State
  const [showAddProvider, setShowAddProvider] = useState(false);
  const [categoriesList, setCategoriesList] = useState([]);
  const [addProviderForm, setAddProviderForm] = useState({
    business_name: '', email: '', contact_person: '', phone: '',
    category_id: '', city: '', province: ''
  });
  const [addingProvider, setAddingProvider] = useState(false);

  // Site Settings State
  const [siteSettings, setSiteSettings] = useState({
    hero_title: '',
    hero_subtitle: '',
    site_name: '',
    contact_email: '',
    contact_phone: '',
    hero_image_url: ''
  });
  const [heroImageFile, setHeroImageFile] = useState(null);
  const [heroPreview, setHeroPreview] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  // Front Page Hero Settings State
  const [heroSettings, setHeroSettings] = useState({
    hero_title: '',
    hero_subtitle: '',
    hero_image_url: '',
    hero_overlay_color: '#000000',
    hero_overlay_opacity: 0.5
  });
  const [heroImageFileNew, setHeroImageFileNew] = useState(null);
  const [heroPreviewNew, setHeroPreviewNew] = useState(null);
  const [savingHeroSettings, setSavingHeroSettings] = useState(false);

  useEffect(() => {
    checkAdminAccess();
    if (activeTab === 'providers') {
      loadProviders();
      loadCategories();
    } else if (activeTab === 'approvals') {
      loadPendingClaims();
    } else if (activeTab === 'settings') {
      loadSiteSettings();
    } else if (activeTab === 'hero') {
      loadHeroSettings();
    }
  }, [activeTab]);

  const checkAdminAccess = async () => {
    try {
      // Prevent access to admin panel when impersonating
      const isImpersonating = localStorage.getItem('impersonating') === 'true';
      if (isImpersonating) {
        navigate('/dashboard');
        return;
      }

      const userData = auth.getCurrentUser();
      if (!userData?.is_super_admin) {
        navigate('/');
        return;
      }
      setUser(userData);
    } catch (error) {
      console.error('Access denied:', error);
      navigate('/');
    }
  };

  const loadProviders = async () => {
    try {
      setLoading(true);
      const response = await admin.getAllProviders();
      setProviders(response.data?.providers || response.providers || []);
    } catch (error) {
      console.error('Error loading providers:', error);
      setMessage({ type: 'error', text: 'Failed to load providers' });
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await categoriesApi.getAll();
      setCategoriesList(response.data?.categories || response.data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadPendingClaims = async () => {
    try {
      setApprovalsLoading(true);
      const response = await claims.getPending();
      setPendingClaims(response.data?.claims || response.data || []);
    } catch (error) {
      console.error('Error loading pending claims:', error);
      setMessage({ type: 'error', text: 'Failed to load pending approvals' });
    } finally {
      setApprovalsLoading(false);
    }
  };

  const handleApproveClaim = async (id) => {
    try {
      await claims.approve(id);
      setMessage({ type: 'success', text: 'Request approved!' });
      loadPendingClaims();
    } catch (error) {
      console.error('Error approving claim:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to approve' });
    }
  };

  const handleRejectClaim = async (id) => {
    if (!window.confirm('Are you sure you want to reject this request?')) return;
    try {
      await claims.reject(id);
      setMessage({ type: 'success', text: 'Request rejected.' });
      loadPendingClaims();
    } catch (error) {
      console.error('Error rejecting claim:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to reject' });
    }
  };

  const handleAddProvider = async (e) => {
    e.preventDefault();
    if (!addProviderForm.business_name || !addProviderForm.email) {
      setMessage({ type: 'error', text: 'Business name and email are required' });
      return;
    }
    try {
      setAddingProvider(true);
      await admin.createProvider(addProviderForm);
      setMessage({ type: 'success', text: 'Provider created successfully!' });
      setAddProviderForm({ business_name: '', email: '', contact_person: '', phone: '', category_id: '', city: '', province: '' });
      setShowAddProvider(false);
      loadProviders();
    } catch (error) {
      console.error('Error creating provider:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to create provider' });
    } finally {
      setAddingProvider(false);
    }
  };

  const loadSiteSettings = async () => {
    try {
      setLoading(true);
      const response = await settings.getAll();
      const settingsData = response.data || {};

      const formattedSettings = {};
      Object.keys(settingsData).forEach(key => {
        formattedSettings[key] = settingsData[key].value || '';
      });

      setSiteSettings(prev => ({ ...prev, ...formattedSettings }));
      setHeroPreview(formattedSettings.hero_image_url);
    } catch (error) {
      console.error('Error loading site settings:', error);
      setMessage({ type: 'error', text: 'Failed to load site settings' });
    } finally {
      setLoading(false);
    }
  };

  const loadHeroSettings = async () => {
    try {
      setLoading(true);
      const response = await settings.getAll();
      const settingsData = response.data || {};

      setHeroSettings({
        hero_title: settingsData.hero_title || '',
        hero_subtitle: settingsData.hero_subtitle || '',
        hero_image_url: settingsData.hero_image_url || '',
        hero_overlay_color: settingsData.hero_overlay_color || '#000000',
        hero_overlay_opacity: settingsData.hero_overlay_opacity || 0.5
      });
      setHeroPreviewNew(settingsData.hero_image_url);
    } catch (error) {
      console.error('Error loading hero settings:', error);
      setMessage({ type: 'error', text: 'Failed to load hero settings' });
    } finally {
      setLoading(false);
    }
  };

  const unpublishProvider = async (providerId, businessName) => {
    if (!window.confirm(`Are you sure you want to unpublish "${businessName}"? They will no longer be visible to couples.`)) {
      return;
    }

    try {
      await admin.unpublishProvider(providerId);
      setMessage({ type: 'success', text: `Successfully unpublished ${businessName}` });
      loadProviders();
    } catch (error) {
      console.error('Error unpublishing provider:', error);
      setMessage({ type: 'error', text: 'Failed to unpublish provider' });
    }
  };

  const deleteProvider = async (providerId, businessName) => {
    const confirmationCode = Math.floor(100000 + Math.random() * 900000).toString();
    const userInput = window.prompt(
      `⚠️ WARNING: This will PERMANENTLY DELETE "${businessName}" and all their data.\n\n` +
      `This action CANNOT be undone!\n\n` +
      `To confirm deletion, enter this code: ${confirmationCode}`
    );

    if (userInput !== confirmationCode) {
      if (userInput !== null) {
        setMessage({ type: 'error', text: 'Incorrect confirmation code. Deletion cancelled.' });
      }
      return;
    }

    try {
      await admin.deleteProvider(providerId, confirmationCode);
      setMessage({ type: 'success', text: `Successfully deleted ${businessName}` });
      loadProviders();
    } catch (error) {
      console.error('Error deleting provider:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to delete provider' });
    }
  };

  const handleImpersonate = async (providerId) => {
    try {
      // Call backend to get real JWT token for the provider
      const response = await admin.impersonate(providerId);

      // Store original admin session
      const originalUser = localStorage.getItem('user');
      const originalToken = localStorage.getItem('token');
      localStorage.setItem('impersonate_original_user', originalUser);
      localStorage.setItem('impersonate_original_token', originalToken);

      // Replace with provider's real token and data
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.provider));
      localStorage.setItem('impersonating', 'true');
      localStorage.setItem('impersonating_type', 'provider');

      // Navigate to dashboard and reload
      navigate('/dashboard');
      window.location.reload();
    } catch (error) {
      console.error('Failed to impersonate provider:', error);
      setMessage({ type: 'error', text: 'Failed to impersonate provider. Please try again.' });
    }
  };

  const handleHeroImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setHeroImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setHeroPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      // Upload hero image if changed
      if (heroImageFile) {
        const formData = new FormData();
        formData.append('hero', heroImageFile);
        const imageResponse = await settings.uploadHeroImage(formData);
        if (imageResponse.data?.imageUrl) {
          siteSettings.hero_image_url = imageResponse.data.imageUrl;
        }
      }

      // Update text settings
      const settingsToUpdate = {
        hero_title: siteSettings.hero_title,
        hero_subtitle: siteSettings.hero_subtitle,
        site_name: siteSettings.site_name,
        contact_email: siteSettings.contact_email,
        contact_phone: siteSettings.contact_phone
      };

      await settings.update(settingsToUpdate);
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
      setHeroImageFile(null);
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSavingSettings(false);
    }
  };

  const handleHeroImageChangeNew = (e) => {
    const file = e.target.files[0];
    if (file) {
      setHeroImageFileNew(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setHeroPreviewNew(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveHeroSettings = async () => {
    setSavingHeroSettings(true);
    try {
      console.log('Saving hero settings. Has new image?', !!heroImageFileNew);

      // Upload hero image if changed
      if (heroImageFileNew) {
        console.log('Uploading hero image:', heroImageFileNew.name);
        const formData = new FormData();
        formData.append('hero', heroImageFileNew);

        try {
          const imageResponse = await settings.uploadHeroImage(formData);
          console.log('Upload response:', imageResponse.data);

          if (imageResponse.data?.imageUrl) {
            heroSettings.hero_image_url = imageResponse.data.imageUrl;
            console.log('Updated hero_image_url to:', heroSettings.hero_image_url);
          }
        } catch (uploadError) {
          console.error('Image upload failed:', uploadError);
          setMessage({ type: 'error', text: 'Failed to upload hero image: ' + (uploadError.response?.data?.error || uploadError.message) });
          setSavingHeroSettings(false);
          return;
        }
      }

      // Update hero settings
      const settingsToUpdate = {
        hero_title: heroSettings.hero_title,
        hero_subtitle: heroSettings.hero_subtitle,
        hero_image_url: heroSettings.hero_image_url,
        hero_overlay_color: heroSettings.hero_overlay_color,
        hero_overlay_opacity: parseFloat(heroSettings.hero_overlay_opacity)
      };

      console.log('Updating settings:', settingsToUpdate);
      await settings.update(settingsToUpdate);
      setMessage({ type: 'success', text: 'Hero settings saved successfully!' });

      // Keep the preview but clear the file object
      setHeroImageFileNew(null);
      // Update state to show the saved image URL
      setHeroSettings({ ...heroSettings });
    } catch (error) {
      console.error('Error saving hero settings:', error);
      setMessage({ type: 'error', text: 'Failed to save hero settings: ' + (error.response?.data?.error || error.message) });
    } finally {
      setSavingHeroSettings(false);
    }
  };

  const filteredProviders = providers.filter(provider => {
    const matchesSearch = provider.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         provider.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         provider.email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  return (
    <div className="min-h-screen" style={{ backgroundColor: weddingColors.ui.background }}>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${weddingColors.accent.burgundy} 0%, ${weddingColors.primary.terracotta} 100%)`,
        padding: '30px',
        color: 'white'
      }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Shield className="w-8 h-8" />
              Admin Control Panel
            </h1>
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors flex items-center gap-2"
              >
                <Home className="w-4 h-4" />
                Home
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors flex items-center gap-2"
              >
                <User className="w-4 h-4" />
                Dashboard
              </button>
            </div>
          </div>
          <p className="text-white/80">Logged in as: {user?.name || user?.email}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white shadow-md sticky top-0 z-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex">
            <button
              onClick={() => setActiveTab('providers')}
              className={`px-6 py-4 font-medium transition-colors flex items-center gap-2 border-b-4 ${
                activeTab === 'providers'
                  ? 'border-terracotta text-terracotta'
                  : 'border-transparent text-gray-600 hover:text-terracotta'
              }`}
            >
              <Users className="w-5 h-5" />
              Providers
            </button>
            <button
              onClick={() => setActiveTab('approvals')}
              className={`px-6 py-4 font-medium transition-colors flex items-center gap-2 border-b-4 ${
                activeTab === 'approvals'
                  ? 'border-terracotta text-terracotta'
                  : 'border-transparent text-gray-600 hover:text-terracotta'
              }`}
            >
              <Clock className="w-5 h-5" />
              Approvals
            </button>
            <button
              onClick={() => setActiveTab('hero')}
              className={`px-6 py-4 font-medium transition-colors flex items-center gap-2 border-b-4 ${
                activeTab === 'hero'
                  ? 'border-terracotta text-terracotta'
                  : 'border-transparent text-gray-600 hover:text-terracotta'
              }`}
            >
              <Image className="w-5 h-5" />
              Front Page Hero Settings
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-6 py-4 font-medium transition-colors flex items-center gap-2 border-b-4 ${
                activeTab === 'settings'
                  ? 'border-terracotta text-terracotta'
                  : 'border-transparent text-gray-600 hover:text-terracotta'
              }`}
            >
              <Settings className="w-5 h-5" />
              Site Settings
            </button>
          </div>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className="max-w-7xl mx-auto px-4 mt-4">
          <div className={`p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success'
              ? 'bg-green-100 text-green-800 border border-green-300'
              : 'bg-red-100 text-red-800 border border-red-300'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            {message.text}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto p-6">
        {loading && (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-terracotta"></div>
          </div>
        )}

        {/* Providers Tab */}
        {!loading && activeTab === 'providers' && (
          <div>
            {/* Stats + Add Provider Button */}
            <div className="flex items-center justify-between mb-6">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <div className="flex items-center justify-between gap-6">
                  <div>
                    <p className="text-gray-600 text-sm">Total Providers</p>
                    <p className="text-3xl font-bold text-gray-900">{providers.length}</p>
                  </div>
                  <Users className="w-10 h-10 text-gray-400" />
                </div>
              </div>
              <button
                onClick={() => setShowAddProvider(!showAddProvider)}
                className="px-5 py-3 bg-terracotta text-white rounded-lg hover:bg-russet transition-colors font-medium flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Add Provider
              </button>
            </div>

            {/* Add Provider Form */}
            {showAddProvider && (
              <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Add New Provider</h3>
                <form onSubmit={handleAddProvider} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Business Name *</label>
                    <input type="text" required value={addProviderForm.business_name}
                      onChange={(e) => setAddProviderForm({ ...addProviderForm, business_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracotta" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input type="email" required value={addProviderForm.email}
                      onChange={(e) => setAddProviderForm({ ...addProviderForm, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracotta" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                    <input type="text" value={addProviderForm.contact_person}
                      onChange={(e) => setAddProviderForm({ ...addProviderForm, contact_person: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracotta" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input type="tel" value={addProviderForm.phone}
                      onChange={(e) => setAddProviderForm({ ...addProviderForm, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracotta" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select value={addProviderForm.category_id}
                      onChange={(e) => setAddProviderForm({ ...addProviderForm, category_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracotta">
                      <option value="">Select category...</option>
                      {categoriesList.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input type="text" value={addProviderForm.city}
                      onChange={(e) => setAddProviderForm({ ...addProviderForm, city: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracotta" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Province</label>
                    <input type="text" value={addProviderForm.province}
                      onChange={(e) => setAddProviderForm({ ...addProviderForm, province: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracotta" />
                  </div>
                  <div className="flex items-end gap-3">
                    <button type="submit" disabled={addingProvider}
                      className="px-5 py-2 bg-terracotta text-white rounded-lg hover:bg-russet transition-colors font-medium disabled:opacity-50">
                      {addingProvider ? 'Creating...' : 'Create Provider'}
                    </button>
                    <button type="button" onClick={() => setShowAddProvider(false)}
                      className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors">
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Search */}
            <div className="mb-6">
              <input
                type="text"
                placeholder="Search providers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracotta"
              />
            </div>

            {/* Providers Table */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-700">Business Name</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-700">Contact</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-700">Category</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredProviders.map(provider => (
                    <tr key={provider.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{provider.business_name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{provider.contact_person}</div>
                        <div className="text-sm text-gray-500">{provider.email}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600">{provider.category_name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={() => navigate(`/provider/${provider.id}`)}
                            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                          >
                            View Profile
                          </button>
                          <button
                            onClick={() => handleImpersonate(provider.id)}
                            className="px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 transition-colors"
                          >
                            Impersonate
                          </button>
                          {provider.is_published && (
                            <button
                              onClick={() => unpublishProvider(provider.id, provider.business_name)}
                              className="px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700 transition-colors"
                            >
                              Unpublish
                            </button>
                          )}
                          <button
                            onClick={() => deleteProvider(provider.id, provider.business_name)}
                            className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Approvals Tab */}
        {!loading && activeTab === 'approvals' && (
          <div>
            <div className="bg-white p-6 rounded-xl shadow-md mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">Pending Requests</p>
                  <p className="text-3xl font-bold text-gray-900">{pendingClaims.length}</p>
                </div>
                <Clock className="w-10 h-10 text-gray-400" />
              </div>
            </div>

            {approvalsLoading ? (
              <div className="flex justify-center items-center h-32">
                <div className="animate-spin rounded-full h-10 w-10 border-b-4 border-terracotta"></div>
              </div>
            ) : pendingClaims.length === 0 ? (
              <div className="bg-white rounded-xl shadow-md p-12 text-center text-gray-500">
                No pending requests.
              </div>
            ) : (
              <div className="space-y-4">
                {pendingClaims.map(item => (
                  <div key={item.id} className="bg-white rounded-xl shadow-md p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
                            item.type === 'claim'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {item.type || 'application'}
                          </span>
                          <span className="text-sm text-gray-500">
                            {item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}
                          </span>
                        </div>

                        {item.type === 'claim' ? (
                          <div>
                            <p className="font-medium text-gray-900">
                              Claim for: <span className="text-terracotta">{item.business_name || item.provider_business_name || 'Unknown business'}</span>
                            </p>
                            <p className="text-sm text-gray-600 mt-1">
                              Claiming email: {item.email || item.claimant_email}
                            </p>
                          </div>
                        ) : (
                          <div>
                            <p className="font-medium text-gray-900">
                              New application: <span className="text-terracotta">{item.business_name}</span>
                            </p>
                            <div className="text-sm text-gray-600 mt-1 space-y-1">
                              <p>Email: {item.email}</p>
                              {item.contact_person && <p>Contact: {item.contact_person}</p>}
                              {item.phone && <p>Phone: {item.phone}</p>}
                              {item.category && <p>Category: {item.category}</p>}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => handleApproveClaim(item.id)}
                          className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1"
                        >
                          <CheckSquare className="w-4 h-4" />
                          Approve
                        </button>
                        <button
                          onClick={() => handleRejectClaim(item.id)}
                          className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors flex items-center gap-1"
                        >
                          <XSquare className="w-4 h-4" />
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Front Page Hero Settings Tab */}
        {!loading && activeTab === 'hero' && (
          <div className="max-w-4xl">
            <div className="bg-white rounded-xl shadow-md p-8">
              <h2 className="text-2xl font-bold mb-6 text-gray-800 flex items-center gap-2">
                <Image className="w-7 h-7" />
                Front Page Hero Settings
              </h2>

              {/* Live Preview */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Live Preview
                </label>
                <div className="relative h-64 rounded-xl overflow-hidden border-2 border-gray-200">
                  {heroPreviewNew ? (
                    <img
                      src={heroPreviewNew.startsWith('data:') ? heroPreviewNew : `https://api.bridalconnect.co.za${heroPreviewNew}`}
                      alt="Hero preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                      <p className="text-gray-500">No image uploaded</p>
                    </div>
                  )}
                  <div
                    className="absolute inset-0 flex flex-col items-center justify-center text-white"
                    style={{
                      backgroundColor: `${heroSettings.hero_overlay_color}${Math.round(heroSettings.hero_overlay_opacity * 255).toString(16).padStart(2, '0')}`
                    }}
                  >
                    <h1 className="text-4xl font-bold mb-2">
                      {heroSettings.hero_title || 'Hero Title'}
                    </h1>
                    <p className="text-xl">
                      {heroSettings.hero_subtitle || 'Hero Subtitle'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Hero Image Upload */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Background Image
                </label>
                <div className="flex gap-4 items-center">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleHeroImageChangeNew}
                    className="hidden"
                    id="hero-upload-new"
                  />
                  <label
                    htmlFor="hero-upload-new"
                    className="px-4 py-2 bg-terracotta text-white rounded-lg cursor-pointer hover:bg-russet transition-colors inline-flex items-center gap-2"
                  >
                    <Upload className="w-4 h-4" />
                    Upload Background Image
                  </label>
                  <p className="text-sm text-gray-500">
                    Recommended: 1920x600px (JPG, PNG, WebP)
                  </p>
                </div>
              </div>

              {/* Hero Title */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hero Title
                </label>
                <input
                  type="text"
                  value={heroSettings.hero_title}
                  onChange={(e) => setHeroSettings({ ...heroSettings, hero_title: e.target.value })}
                  placeholder="Wedding Service Providers"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracotta"
                />
              </div>

              {/* Hero Subtitle */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hero Subtitle
                </label>
                <input
                  type="text"
                  value={heroSettings.hero_subtitle}
                  onChange={(e) => setHeroSettings({ ...heroSettings, hero_subtitle: e.target.value })}
                  placeholder="Find trusted professionals for your perfect day"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracotta"
                />
              </div>

              {/* Overlay Color */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Overlay Color
                </label>
                <div className="flex gap-3 items-center">
                  <input
                    type="color"
                    value={heroSettings.hero_overlay_color}
                    onChange={(e) => setHeroSettings({ ...heroSettings, hero_overlay_color: e.target.value })}
                    className="h-12 w-20 rounded-lg border border-gray-200 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={heroSettings.hero_overlay_color}
                    onChange={(e) => setHeroSettings({ ...heroSettings, hero_overlay_color: e.target.value })}
                    placeholder="#000000"
                    className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracotta"
                  />
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Choose the color overlay for the hero background image
                </p>
              </div>

              {/* Overlay Opacity */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Overlay Transparency: {Math.round(heroSettings.hero_overlay_opacity * 100)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={heroSettings.hero_overlay_opacity}
                  onChange={(e) => setHeroSettings({ ...heroSettings, hero_overlay_opacity: parseFloat(e.target.value) })}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, transparent 0%, ${heroSettings.hero_overlay_color} 100%)`
                  }}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Transparent (0%)</span>
                  <span>Opaque (100%)</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Adjust the transparency of the overlay (higher = more opaque)
                </p>
              </div>

              {/* Save Button */}
              <div className="flex justify-end">
                <button
                  onClick={handleSaveHeroSettings}
                  disabled={savingHeroSettings}
                  className="px-6 py-3 bg-terracotta text-white rounded-lg hover:bg-russet transition-colors font-medium flex items-center gap-2 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {savingHeroSettings ? 'Saving...' : 'Save Hero Settings'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Site Settings Tab */}
        {!loading && activeTab === 'settings' && (
          <div className="max-w-4xl">
            <div className="bg-white rounded-xl shadow-md p-8">
              <h2 className="text-2xl font-bold mb-6 text-gray-800">Site Settings</h2>
              <p className="text-sm text-gray-600 mb-6">
                Note: Hero banner settings have been moved to the "Front Page Hero Settings" tab.
              </p>

              {/* Text Settings */}
              <div className="space-y-6">

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Site Name
                  </label>
                  <input
                    type="text"
                    value={siteSettings.site_name}
                    onChange={(e) => setSiteSettings({ ...siteSettings, site_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracotta"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    value={siteSettings.contact_email}
                    onChange={(e) => setSiteSettings({ ...siteSettings, contact_email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracotta"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    value={siteSettings.contact_phone}
                    onChange={(e) => setSiteSettings({ ...siteSettings, contact_phone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-terracotta"
                  />
                </div>
              </div>

              {/* Save Button */}
              <div className="mt-8 flex justify-end">
                <button
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                  className="px-6 py-3 bg-terracotta text-white rounded-lg hover:bg-russet transition-colors font-medium flex items-center gap-2 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {savingSettings ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default EnhancedAdminPanel;